import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Download, ChevronDown, Check, Loader2, AlertTriangle } from 'lucide-react';
import { useProducts } from '../contexts/ProductContext';
import { useMachines } from '../contexts/MachineContext';
import { useFonts } from '../contexts/FontContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import UpgradePrompt from './UpgradePrompt';
import * as historyService from '../services/historyService';
import SvgPreview from '@jigflow/shared/components/SvgPreview';
import { extractGraphicSvgForPreview } from '@jigflow/shared/generators/svgGenerator';
import type {
  Product,
  ProductTemplate,
  TemplateShape,
  ShapeProcessSettings,
  CartItem,
  Font,
} from '@jigflow/shared';

interface ProcessingModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: CartItem;
  onComplete: () => void;
}

interface ExtendedField {
  id: string;
  label: string;
  type: string;
  options?: string[];
  maxLength?: number;
  score?: boolean;
  engrave?: boolean;
  fontFamily?: string;
}

export default function ProcessingModal({ isOpen, onClose, item, onComplete }: ProcessingModalProps) {
  const {
    products,
    getFieldsByProductId,
    getTemplateByProductId,
    getShapesByTemplateId,
    getVariationsByProductId,
    getProcessSettingsByShapeId,
  } = useProducts();
  const { machines } = useMachines();
  const { fonts } = useFonts();
  const { workspace, usage, tierLimits, tier, isWithinLimit, refreshWorkspace } = useWorkspace();

  const activeMachine = machines.find(m => m.active) || machines[0] || null;

  // Download metering
  const downloadsThisMonth = usage?.downloadCount ?? 0;
  const downloadLimit = tierLimits.downloadsPerMonth;
  const canDownload = isWithinLimit('downloadsPerMonth', downloadsThisMonth);

  const [product, setProduct] = useState<Product | null>(null);
  const [fields, setFields] = useState<ExtendedField[]>([]);
  const [template, setTemplate] = useState<ProductTemplate | null>(null);
  const [shapes, setShapes] = useState<TemplateShape[]>([]);
  const [processSettings, setProcessSettings] = useState<Map<string, Map<string, ShapeProcessSettings>>>(new Map());
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const [downloadFormat, setDownloadFormat] = useState<'svg' | 'xcs' | 'lbrn'>(() => {
    const saved = localStorage.getItem('preferredDownloadFormat');
    return (saved === 'svg' || saved === 'xcs' || saved === 'lbrn') ? saved : 'xcs';
  });
  const [formatDropdownOpen, setFormatDropdownOpen] = useState(false);
  const formatDropdownRef = useRef<HTMLDivElement>(null);

  const handleFormatChange = (format: 'svg' | 'xcs' | 'lbrn') => {
    setDownloadFormat(format);
    localStorage.setItem('preferredDownloadFormat', format);
    setFormatDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (formatDropdownRef.current && !formatDropdownRef.current.contains(event.target as Node)) {
        setFormatDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate scale to fit SvgPreview in container
  const calculatePreviewScale = useCallback(() => {
    if (!previewContainerRef.current || (!template && !product)) return;

    const container = previewContainerRef.current;
    const padding = 48;
    const availableWidth = container.clientWidth - padding;
    const availableHeight = container.clientHeight - padding;

    const contentWidth = template?.width || activeMachine?.width || 300;
    const contentHeight = template?.height || activeMachine?.height || 300;

    const scaleX = availableWidth / contentWidth;
    const scaleY = availableHeight / contentHeight;
    setPreviewScale(Math.min(scaleX, scaleY));
  }, [template, product, activeMachine]);

  // Recalculate on resize or data load
  useEffect(() => {
    if (!loading) {
      calculatePreviewScale();
      const resizeObserver = new ResizeObserver(calculatePreviewScale);
      if (previewContainerRef.current) {
        resizeObserver.observe(previewContainerRef.current);
      }
      return () => resizeObserver.disconnect();
    }
  }, [loading, calculatePreviewScale]);

  useEffect(() => {
    if (isOpen && item) {
      loadData();
    }
  }, [isOpen, item]);

  const loadData = async () => {
    setLoading(true);

    const p = products.find(prod => prod.id === item.productId) || null;
    setProduct(p);

    if (!p) {
      setLoading(false);
      return;
    }

    try {
      const [dbFields, variations] = await Promise.all([
        getFieldsByProductId(p.id),
        getVariationsByProductId(p.id),
      ]);

      const fontSelections = item.fontSelections || {};
      const convertedFields: ExtendedField[] = dbFields.map(f => {
        let parsedOptions: string[] | undefined;
        if (f.options) {
          try {
            parsedOptions = typeof f.options === 'string' ? JSON.parse(f.options as string) : f.options;
          } catch {
            parsedOptions = undefined;
          }
        }
        const fontFamily = fontSelections[f.id] || f.fontFamily || 'Arial';
        return {
          id: f.id,
          label: f.label,
          type: f.type === 'checkbox' ? 'text' : f.type as string,
          options: parsedOptions,
          maxLength: f.maxLength,
          score: f.score ?? false,
          engrave: f.engrave ?? true,
          fontFamily,
        };
      });
      setFields(convertedFields);

      // Determine variation for template lookup
      let variationId: string | undefined = undefined;
      if (variations.length > 0) {
        const variationField = dbFields.find(f => f.label === '__variation_selector__');
        if (variationField) {
          const selectedVariationName = item.customizations[variationField.id];
          if (selectedVariationName) {
            const selectedVariation = variations.find(v => v.name === selectedVariationName);
            if (selectedVariation) variationId = selectedVariation.id;
          }
        }
      }

      const loadedTemplate = await getTemplateByProductId(p.id, variationId);
      setTemplate(loadedTemplate);

      if (loadedTemplate) {
        const loadedShapes = await getShapesByTemplateId(loadedTemplate.id);
        setShapes(loadedShapes);

        const settingsMap = new Map<string, Map<string, ShapeProcessSettings>>();
        for (const shape of loadedShapes) {
          const shapeSettings = await getProcessSettingsByShapeId(shape.id);
          if (shapeSettings.length > 0) {
            const operationMap = new Map<string, ShapeProcessSettings>();
            shapeSettings.forEach(s => operationMap.set(s.operation, s));
            settingsMap.set(shape.id, operationMap);
          }
        }
        setProcessSettings(settingsMap);
      } else {
        setShapes([]);
        setProcessSettings(new Map());
      }
    } catch (error) {
      console.error('[Processing] Failed to load product data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = useCallback(async (format: 'svg' | 'xcs' | 'lbrn' = 'svg') => {
    setDownloading(true);
    try {
      const contentWidth = template?.width || activeMachine?.width || 300;
      const contentHeight = template?.height || activeMachine?.height || 300;

      let content: string;
      let filename: string;
      let mimeType: string;

      // Dynamic imports for generators from @jigflow/shared
      if (format === 'xcs') {
        const { buildXcs, getLaserTypeFromMachine } = await import('@jigflow/shared/generators/xcsGenerator');
        content = await buildXcs(shapes, fields as any, item.customizations, contentWidth, contentHeight, {
          fonts: fonts as Font[],
          laserType: activeMachine ? getLaserTypeFromMachine(activeMachine) : undefined,
          processSettings: processSettings.size > 0 ? processSettings : undefined,
        });
        filename = `${item.productName}-${item.id}.xcs`;
        mimeType = 'application/json';
      } else if (format === 'lbrn') {
        const { buildLbrn } = await import('@jigflow/shared/generators/lbrnGenerator');
        content = await buildLbrn(shapes as any, fields as any, item.customizations, contentWidth, contentHeight, {
          fonts: fonts as Font[],
          processSettings: processSettings.size > 0 ? processSettings as any : undefined,
        });
        filename = `${item.productName}-${item.id}.lbrn`;
        mimeType = 'application/xml';
      } else {
        const { buildSvgAsync } = await import('@jigflow/shared/generators/svgGenerator');
        content = await buildSvgAsync(shapes, fields as any, item.customizations, contentWidth, contentHeight, fonts as Font[]);
        filename = `${item.productName}-${item.id}.svg`;
        mimeType = 'image/svg+xml';
      }

      if (!content) return;

      // Browser Blob download
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Record download in history + refresh usage count
      if (workspace?.id) {
        try {
          await historyService.recordDownload(workspace.id, {
            productId: item.productId,
            productName: item.productName,
            customizations: item.customizations,
            format,
          }, { blob, filename });
          // Refresh workspace to update download count
          await refreshWorkspace();
        } catch (err) {
          console.error('[Processing] Failed to record download:', err);
        }
      }

      onComplete();
      onClose();
    } catch (error) {
      console.error('[Processing] Download failed:', error);
    } finally {
      setDownloading(false);
    }
  }, [template, activeMachine, shapes, fields, item, fonts, processSettings, workspace, refreshWorkspace, onComplete, onClose]);

  if (!isOpen) return null;

  const contentWidth = template?.width || activeMachine?.width || 300;
  const contentHeight = template?.height || activeMachine?.height || 300;

  const formatLabels: Record<string, string> = {
    svg: 'SVG',
    xcs: 'XCS (xTool)',
    lbrn: 'LBRN (LightBurn)',
  };

  const formatColors: Record<string, string> = {
    xcs: 'bg-green-600 hover:bg-green-700',
    lbrn: 'bg-orange-600 hover:bg-orange-700',
    svg: 'bg-blue-600 hover:bg-blue-700',
  };

  // Render template SVG with shape zone outlines (left panel)
  const renderTemplatePreview = () => {
    return (
      <div
        className="relative bg-gray-50 dark:bg-gray-900 rounded overflow-hidden"
        style={{ aspectRatio: `${contentWidth}/${contentHeight}` }}
      >
        {/* Background SVG at 30% opacity - skip when graphic shapes exist (they render in the overlay) */}
        {template?.svgContent && !shapes.some(s => s.type === 'graphic') && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ opacity: 0.3 }}
            dangerouslySetInnerHTML={{ __html: template.svgContent }}
          />
        )}
        {/* Shape zone outlines + raster images overlay */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={`0 0 ${contentWidth} ${contentHeight}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {shapes.map(s => {
            const transform = s.rotation
              ? `rotate(${s.rotation} ${s.x + s.width / 2} ${s.y + s.height / 2})`
              : undefined;

            // Render raster images directly
            if (s.type === 'raster' && s.rasterData) {
              return (
                <image
                  key={s.id}
                  x={s.x}
                  y={s.y}
                  width={s.width}
                  height={s.height}
                  href={s.rasterData}
                  preserveAspectRatio="none"
                  transform={transform}
                />
              );
            }

            // Render graphic shapes with their SVG content
            if (s.type === 'graphic' && s.svgData) {
              const extracted = extractGraphicSvgForPreview(s.svgData, s);
              if (!extracted) return null;

              return (
                <g key={s.id} transform={transform}>
                  <g
                    transform={extracted.transform}
                    dangerouslySetInnerHTML={{ __html: extracted.innerContent }}
                  />
                </g>
              );
            }

            // Render rect shapes with field labels
            if (s.type === 'rect') {
              const field = s.fieldId ? fields.find(f => f.id === s.fieldId) : null;
              const label = field?.label || '';
              const fontSize = label
                ? Math.min(s.height * 0.6, s.width / Math.max(label.length * 0.5, 1))
                : 12;

              return (
                <g key={s.id} transform={transform}>
                  <rect
                    x={s.x}
                    y={s.y}
                    width={s.width}
                    height={s.height}
                    fill={field ? 'rgba(59, 130, 246, 0.1)' : 'none'}
                    stroke={field ? '#3b82f6' : '#666'}
                    strokeWidth="0.5"
                    strokeDasharray={field ? undefined : '2,2'}
                  />
                  {label && (
                    <text
                      x={s.x + s.width / 2}
                      y={s.y + s.height / 2}
                      fontSize={fontSize}
                      fill="#3b82f6"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontWeight="500"
                    >
                      {label}
                    </text>
                  )}
                </g>
              );
            }

            // Fallback: dashed outline for unknown shapes
            return (
              <rect
                key={s.id}
                x={s.x}
                y={s.y}
                width={s.width}
                height={s.height}
                fill="none"
                stroke="#666"
                strokeWidth="0.5"
                strokeDasharray="2,2"
                transform={transform}
              />
            );
          })}
        </svg>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[95vw] max-w-[1600px] h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-white">{item.productName}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Preview output before downloading</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - 2 column layout */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Loading template data...</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* LEFT: Template + Customizations - 35% */}
            <div className="w-[35%] shrink-0 p-3 border-r border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden bg-gray-100 dark:bg-gray-950">
              {/* Template Preview */}
              <div className="shrink-0 mb-3">
                <h4 className="text-xs font-semibold mb-2 text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Template
                </h4>
                {renderTemplatePreview()}
              </div>

              {/* Customizations - scrollable */}
              <div className="flex-1 overflow-y-auto min-h-0">
                <h4 className="text-xs font-semibold mb-2 text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Customizations
                </h4>
                <div className="space-y-2">
                  {fields.map(field => {
                    const value = item.customizations[field.id] || '';
                    if (!value || field.label === '__variation_selector__') return null;

                    return (
                      <div key={field.id} className="bg-white dark:bg-gray-800 rounded p-2">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {field.label}
                        </div>
                        <div className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                          {value}
                        </div>
                        {field.fontFamily && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            Font: {field.fontFamily}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Machine info */}
                {activeMachine && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-xs font-semibold mb-1 text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Machine
                    </h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {activeMachine.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {activeMachine.width} x {activeMachine.height} mm
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: Live Output Preview - 65% */}
            <div className="flex-1 p-3 bg-white dark:bg-gray-800 flex flex-col min-h-0">
              <h4 className="text-xs font-semibold mb-2 text-gray-500 dark:text-gray-400 uppercase tracking-wide shrink-0">
                Output Preview
              </h4>
              <div
                ref={previewContainerRef}
                className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-lg shadow-inner flex items-center justify-center p-3 min-h-0 overflow-hidden"
              >
                {shapes.length > 0 ? (
                  <div
                    style={{
                      transform: `scale(${previewScale})`,
                      transformOrigin: 'center center',
                    }}
                  >
                    <SvgPreview
                      width={contentWidth}
                      height={contentHeight}
                      fields={fields as any}
                      values={item.customizations}
                      template={template}
                      shapes={shapes}
                      showBackground={true}
                      className="shadow-lg"
                      fonts={fonts as Font[]}
                    />
                  </div>
                ) : (
                  <div className="text-center text-gray-400">
                    <p>No template configured</p>
                    <p className="text-xs mt-1">Set up a template in Settings to see a preview</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer - download controls */}
        <div className="shrink-0 p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          {!canDownload ? (
            /* At download limit - show upgrade prompt */
            <UpgradePrompt
              feature="downloads"
              currentCount={downloadsThisMonth}
              limit={downloadLimit}
              compact
            />
          ) : (
            <div className="flex items-center justify-center gap-3">
              {/* Download usage indicator */}
              {downloadLimit !== Infinity && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {downloadsThisMonth >= downloadLimit * 0.8 && (
                    <AlertTriangle size={12} className="text-amber-500" />
                  )}
                  <span>{downloadsThisMonth}/{downloadLimit} downloads this month</span>
                </div>
              )}

              <div className="relative inline-flex" ref={formatDropdownRef}>
                {/* Main download button */}
                <button
                  onClick={() => handleDownload(downloadFormat)}
                  disabled={downloading || !shapes.length}
                  className={`py-2 px-4 font-bold rounded-l-lg flex items-center gap-2 transition text-sm text-white disabled:bg-gray-400 ${
                    !downloading && shapes.length ? formatColors[downloadFormat] : ''
                  }`}
                >
                  {downloading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      Download {downloadFormat.toUpperCase()}
                    </>
                  )}
                </button>

                {/* Dropdown toggle */}
                <button
                  onClick={() => setFormatDropdownOpen(!formatDropdownOpen)}
                  className={`py-2 px-2 font-bold rounded-r-lg border-l border-white/20 transition text-white ${formatColors[downloadFormat]}`}
                >
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${formatDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Dropdown menu */}
                {formatDropdownOpen && (
                  <div className="absolute bottom-full mb-2 right-0 bg-white dark:bg-gray-700 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 overflow-hidden min-w-[160px] z-10">
                    {(['xcs', 'lbrn', 'svg'] as const).map(fmt => (
                      <button
                        key={fmt}
                        onClick={() => handleFormatChange(fmt)}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between gap-2 transition ${
                          downloadFormat === fmt
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        <span className="font-medium">{formatLabels[fmt]}</span>
                        {downloadFormat === fmt && <Check size={14} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Close button */}
              <button
                onClick={onClose}
                className="py-2 px-4 font-bold rounded-lg bg-gray-600 hover:bg-gray-700 text-white flex items-center gap-2 transition text-sm"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
